/**
 * useQueueRealtime Hook
 * 
 * Provides live updates for the global queue view via Supabase realtime.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  fetchGlobalQueue, 
  getGlobalQueueStats, 
  fetchCrawlLogs, 
  fetchActiveJobRuns,
  fetchDiscoveryTasks,
  fetchDiscoveryStats,
} from '@/lib/api/queue';
import type { 
  QueueItemWithRelations, 
  QueueFilters, 
  QueueStats,
  CrawlLogEntry,
  DiscoveryTask,
} from '@/lib/api/queue';

const PAGE_SIZE = 50;

export function useQueueRealtime(filters: QueueFilters = {}) {
  const [items, setItems] = useState<QueueItemWithRelations[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<QueueStats>({ total: 0, pending: 0, in_progress: 0, completed: 0, failed: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const loadQueue = useCallback(async (page?: number) => {
    try {
      setIsLoading(true);
      const pageToUse = page ?? currentPage;
      const offset = (pageToUse - 1) * PAGE_SIZE;

      const [queueResult, statsResult] = await Promise.all([
        fetchGlobalQueue({ filters: filtersRef.current, limit: PAGE_SIZE, offset }),
        getGlobalQueueStats(),
      ]);

      setItems(queueResult.data);
      setTotalCount(queueResult.count);
      setStats(statsResult);
    } catch (error) {
      console.error('Failed to load queue:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage]);

  // Initial load and filter changes
  useEffect(() => {
    setCurrentPage(1);
    loadQueue(1);
  }, [loadQueue]);

  // Page changes
  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  // Realtime subscription for queue changes
  useEffect(() => {
    const channel = supabase
      .channel('global_queue_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'scrape_queue',
        },
        () => {
          // Debounce: only refresh if it's been >500ms since last update
          loadQueue();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadQueue]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return {
    items,
    totalCount,
    stats,
    currentPage,
    totalPages,
    pageSize: PAGE_SIZE,
    isLoading,
    setCurrentPage,
    refetch: loadQueue,
  };
}

/**
 * useLiveMonitor Hook
 * 
 * Provides live crawl logs and active run monitoring.
 */
const LOG_PAGE_SIZE = 200;

export function useLiveMonitor() {
  const [logs, setLogs] = useState<CrawlLogEntry[]>([]);
  const [logsTotalCount, setLogsTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [activeRuns, setActiveRuns] = useState<Array<{
    id: string;
    job_id: string;
    status: string;
    phase: string | null;
    started_at: string;
    urls_discovered: number | null;
    urls_scraped: number | null;
    records_scraped: number | null;
    metadata: Record<string, unknown> | null;
    last_activity_at: string | null;
    scrape_jobs: { id: string; name: string } | null;
  }>>([]);
  const [discoveryTasks, setDiscoveryTasks] = useState<DiscoveryTask[]>([]);
  const [discoveryStats, setDiscoveryStats] = useState({ total: 0, pending: 0, inProgress: 0, completed: 0, failed: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [logLevel, setLogLevel] = useState<string>('all');
  const [autoScroll, setAutoScroll] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [logsResult, runsResult, dtResult, dsResult] = await Promise.all([
        fetchCrawlLogs({ level: logLevel !== 'all' ? logLevel : undefined, limit: LOG_PAGE_SIZE }),
        fetchActiveJobRuns(),
        fetchDiscoveryTasks(),
        fetchDiscoveryStats(),
      ]);
      setLogs(logsResult.data);
      setLogsTotalCount(logsResult.count);
      setActiveRuns(runsResult);
      setDiscoveryTasks(dtResult);
      setDiscoveryStats(dsResult);
    } catch (error) {
      console.error('Failed to load monitor data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [logLevel]);

  const loadMoreLogs = useCallback(async () => {
    try {
      setIsLoadingMore(true);
      const result = await fetchCrawlLogs({
        level: logLevel !== 'all' ? logLevel : undefined,
        limit: LOG_PAGE_SIZE,
        offset: logs.length,
      });
      setLogs(prev => [...prev, ...result.data]);
      setLogsTotalCount(result.count);
    } catch (error) {
      console.error('Failed to load more logs:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [logLevel, logs.length]);

  const hasMoreLogs = logs.length < logsTotalCount;

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Polling fallback keeps monitor accurate if realtime events are dropped.
  useEffect(() => {
    const pollId = window.setInterval(() => {
      fetchActiveJobRuns().then(setActiveRuns).catch(console.error);
      fetchDiscoveryTasks().then(setDiscoveryTasks).catch(console.error);
      fetchDiscoveryStats().then(setDiscoveryStats).catch(console.error);
    }, 15000);

    return () => {
      window.clearInterval(pollId);
    };
  }, []);

  // Realtime subscription for new logs
  useEffect(() => {
    const logsChannel = supabase
      .channel('crawl_logs_live')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'crawl_logs',
        },
        (payload) => {
          const newLog = payload.new as CrawlLogEntry;
          if (logLevel === 'all' || newLog.level === logLevel) {
            setLogs(prev => [newLog, ...prev].slice(0, 200));
          }
        }
      )
      .subscribe();

    // Subscribe to job_runs changes for active run updates
    const runsChannel = supabase
      .channel('monitor_job_runs')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'job_runs',
        },
        () => {
          fetchActiveJobRuns().then(setActiveRuns).catch(console.error);
        }
      )
      .subscribe();

    // Subscribe to discovery_tasks changes for live progress
    const discoveryChannel = supabase
      .channel('monitor_discovery_tasks')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'discovery_tasks',
        },
        () => {
          fetchDiscoveryTasks().then(setDiscoveryTasks).catch(console.error);
          fetchDiscoveryStats().then(setDiscoveryStats).catch(console.error);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(logsChannel);
      supabase.removeChannel(runsChannel);
      supabase.removeChannel(discoveryChannel);
    };
  }, [logLevel]);

  return {
    logs,
    activeRuns,
    discoveryTasks,
    discoveryStats,
    isLoading,
    isLoadingMore,
    hasMoreLogs,
    logLevel,
    setLogLevel,
    autoScroll,
    setAutoScroll,
    refetch: loadData,
    loadMoreLogs,
  };
}
